import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatMoney } from "../../lib/currency";
import CostTrend from "./CostTrend";

export function CostStat({ label, value, sub, testid, tone = "" }) {
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase text-neutral-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function PeopleTable({ people }) {
  if (!people.length) return <div className="p-4 text-sm text-neutral-500">No hours logged on this project's tasks yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] font-mono uppercase text-neutral-500 text-left">
          <th className="p-3">Person</th><th className="p-3 text-right">Hours</th><th className="p-3 text-right">Rate / hr</th><th className="p-3 text-right">Cost</th>
        </tr></thead>
        <tbody className="divide-y divide-[#E4E7EC]">
          {people.map(p => (
            <tr key={p.user_id || "none"} data-testid={`cost-person-${p.user_id || "none"}`}>
              <td className="p-3">{p.name} <span className="text-[10px] font-mono text-neutral-500">{p.role}</span></td>
              <td className="p-3 text-right font-mono">{p.hours}</td>
              <td className="p-3 text-right font-mono">{p.has_salary ? formatMoney(p.hourly_rate, "INR") : <span className="text-amber-600">no salary set</span>}</td>
              <td className="p-3 text-right font-mono font-semibold">{formatMoney(p.cost, "INR")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProjectCostPanel({ projectId }) {
  const [p, setP] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.get(`/project-costs?project_id=${projectId}`).then(r => setP(r.data.projects[0] || null)).catch(() => setFailed(true)); }, [projectId]);
  if (failed) return <div className="panel p-6 text-sm text-rose-600" data-testid="project-cost-error">Couldn't load team costs. Please refresh and try again.</div>;
  if (!p) return <div className="panel p-6 text-sm text-neutral-500">Loading costs…</div>;
  const inr = (p.currency || "USD") === "INR";
  const left = (p.estimated_budget || 0) - p.people_cost;
  return (
    <div className="space-y-4" data-testid="project-cost-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CostStat label="Team cost" value={formatMoney(p.people_cost, "INR")} sub="hours × hourly rate" testid="cost-total" />
        <CostStat label="Hours logged" value={p.hours} sub={p.unpriced_hours ? `${p.unpriced_hours}h without a salary` : "all hours priced"} testid="cost-hours" />
        <CostStat label="Budget" value={formatMoney(p.estimated_budget || 0, p.currency || "USD")} testid="cost-budget" />
        <CostStat label={inr ? (left >= 0 ? "Budget left" : "Over budget") : "Budget left"} value={inr ? formatMoney(Math.abs(left), "INR") : "—"}
          sub={inr ? `${p.estimated_budget ? Math.round(p.people_cost / p.estimated_budget * 100) : 0}% of budget used` : "budget isn't in ₹, so not compared"}
          tone={inr && left < 0 ? "text-rose-600" : ""} testid="cost-left" />
      </div>
      <CostTrend months={p.months} testid="project-cost-trend" />
      <div className="panel"><PeopleTable people={p.people} /></div>
      <div className="text-[10px] font-mono text-neutral-500">Uses each person's current salary. Hours come from task work logs; task hours entered without a work log are split between the task's assignees.</div>
    </div>
  );
}
